import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
  inject,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { HttpClient } from '@angular/common/http';

import { GooglePlaceAutocompleteComponent } from '@app/shared/google-places/google-place-autocomplete.component';
import { GooglePlaceAddress } from '@app/shared/google-places/google-place-options';
import {
  geoBoundsFromLiteral,
  serializeGeoBounds,
} from '@app/listings/featured-properties/featured-properties-bounds.util';
import { ResidentialCardComponent } from '@app/listings/residential-card/residential-card.component';
import { ListingTypeFilter } from '@app/listings/featured-properties/featured-properties-filters.util';
import { fetchBrowseListingsPage } from '@app/listings/featured-properties/browse-listings-api.util';
import { DEFAULT_FILTER_URL_STATE } from '@app/listings/featured-properties/featured-properties-filter-url.util';
import { ListingType } from '@app/listings/featured-properties/listing-type';
import { PropertyRecord } from '@app/models/property.model';
import { environment } from '@env/environment';
import { propertyTypeFrom } from '@app/utils/property-card.util';
import { ASSET_PATHS } from '@app/constants/asset-paths';

const HOME_FEATURED_TYPES: readonly ListingType[] = [
  'residential',
  'commercial',
  'industrial',
  'land',
];
const HOME_FEATURED_DISPLAY_LIMIT = 6;
/** Per-type fetch cap; home only displays HOME_FEATURED_DISPLAY_LIMIT total. */
const HOME_FEATURED_FETCH_LIMIT = 6;

interface HomeResource {
  title: string;
  description: string;
  link: string;
  linkLabel: string;
}

interface PropertyTypeLink {
  label: string;
  type: ListingTypeFilter;
}

interface ZillowReview {
  reviewDate: string;
  reviewer: string;
  reviewSummary: string;
  description: string;
  reviewURL: string;
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    MatButtonModule,
    GooglePlaceAutocompleteComponent,
    ResidentialCardComponent,
  ],
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeComponent implements OnInit, OnDestroy {
  readonly heroImageWebp800 = ASSET_PATHS.images.heroes.homeLaredoAerialWebp800;
  readonly heroImageWebp1600 = ASSET_PATHS.images.heroes.homeLaredoAerialWebp1600;
  readonly heroImageSrcset = `${this.heroImageWebp800} 800w, ${this.heroImageWebp1600} 1600w`;

  readonly highlights = [
    'Local team rooted in Laredo & South Texas',
    'Homes, land, commercial & industrial in one search',
    'Buyer and seller support from first call to closing',
  ];

  readonly propertyTypeLinks: PropertyTypeLink[] = [
    { label: 'Homes', type: 'residential' },
    { label: 'Commercial', type: 'commercial' },
    { label: 'Land', type: 'land' },
    { label: 'Industrial', type: 'industrial' },
  ];

  readonly resources: HomeResource[] = [
    {
      title: "Buyer's Guide",
      description: 'Step-by-step help for budgeting, searching, and closing on your next home.',
      link: '/buyer-guide',
      linkLabel: 'Read the guide',
    },
    {
      title: "Seller's Guide",
      description: 'Prep, pricing, marketing, and closing — what to expect when you list with Gabali.',
      link: '/seller-guide',
      linkLabel: 'Read the guide',
    },
    {
      title: "What's My Home Worth?",
      description: 'Start with a free estimate, then talk with an agent when you are ready to list.',
      link: '/home-estimate-page',
      linkLabel: 'Get estimate',
    },
    {
      title: 'Meet our agents',
      description:
        'From first-time buyers to investors and sellers, our team lives and works in the communities you are searching.',
      link: '/agents',
      linkLabel: 'Meet the team',
    },
    {
      title: 'Contact Us',
      description: 'Questions about buying, selling, or investing? We will follow up personally.',
      link: '/contact-page',
      linkLabel: 'Get in touch',
    },
  ];

  formattedAddress = '';
  featuredListings: PropertyRecord[] = [];
  listingsLoading = true;

  reviewsLoading = true;
  avgStarRating = 0;
  avgStarPercent = 0;
  totalReviewCount = 0;
  zillowReviews: ZillowReview[] = [];
  profileURL = '';
  activeReviewIndex = 0;
  isReviewDragging = false;
  reviewDragOffset = 0;

  readonly placeOptions = {
    componentRestrictions: {
      country: 'US',
    },
  };

  @ViewChild('placesRef') placesRef!: GooglePlaceAutocompleteComponent;

  private selectedPlace?: GooglePlaceAddress;
  private reviewDragStartX = 0;
  private readonly reviewSwipeThreshold = 50;
  private readonly platformId = inject(PLATFORM_ID);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    void this.loadFeaturedListings();

    this.http.get(environment.API_URL + 'reviews').subscribe({
      next: (response) => {
        const res = response as {
          avgStarRating: number;
          totalReviewCount: number;
          profileURL: string;
          reviews: ZillowReview[];
        };
        this.avgStarRating = res.avgStarRating;
        this.totalReviewCount = res.totalReviewCount;
        this.profileURL = res.profileURL;
        this.zillowReviews = res.reviews ?? [];
        this.avgStarPercent = (this.avgStarRating / 5) * 100;
        this.reviewsLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.reviewsLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  propertyTypeFor(listing: PropertyRecord): string {
    return propertyTypeFrom(listing as Record<string, unknown>);
  }

  handleAddressChange(address: GooglePlaceAddress): void {
    this.formattedAddress = address.formatted_address;
    this.selectedPlace = address;
    this.cdr.markForCheck();
  }

  onSearchInput(value: string): void {
    this.formattedAddress = value;
    this.cdr.markForCheck();
  }

  submitAddress(): void {
    const address = this.placesRef?.getValue()?.trim();
    const queryParams: Record<string, string> = {};
    if (address) {
      queryParams['address'] = address;
    }
    if (this.selectedPlace?.viewport) {
      const bounds = geoBoundsFromLiteral(this.selectedPlace.viewport);
      const serialized = serializeGeoBounds(bounds);
      if (serialized) {
        queryParams['bounds'] = serialized;
      }
    }
    this.router.navigate(['/featured-properties'], { queryParams });
  }

  setActiveReview(index: number): void {
    this.activeReviewIndex = index;
    this.reviewDragOffset = 0;
    this.cdr.markForCheck();
  }

  showPreviousReview(): void {
    if (this.activeReviewIndex > 0) {
      this.setActiveReview(this.activeReviewIndex - 1);
    }
  }

  showNextReview(): void {
    if (this.activeReviewIndex < this.zillowReviews.length - 1) {
      this.setActiveReview(this.activeReviewIndex + 1);
    }
  }

  getReviewTrackTransform(): string {
    const count = this.zillowReviews.length || 1;
    const slidePercent = (this.activeReviewIndex * 100) / count;
    return `translateX(calc(-${slidePercent}% + ${this.reviewDragOffset}px))`;
  }

  onReviewDragStart(event: TouchEvent | MouseEvent): void {
    if (!(event instanceof TouchEvent)) {
      return;
    }

    if (this.zillowReviews.length < 2) {
      return;
    }

    const target = event.target as HTMLElement;
    if (target.closest('a, button')) {
      return;
    }

    this.isReviewDragging = true;
    this.reviewDragStartX = this.getReviewClientX(event);
    this.reviewDragOffset = 0;
  }

  onReviewDragMove(event: TouchEvent | MouseEvent): void {
    if (!this.isReviewDragging || !(event instanceof TouchEvent)) {
      return;
    }

    const delta = this.getReviewClientX(event) - this.reviewDragStartX;
    const atStart = this.activeReviewIndex === 0 && delta > 0;
    const atEnd = this.activeReviewIndex === this.zillowReviews.length - 1 && delta < 0;
    this.reviewDragOffset = atStart || atEnd ? delta * 0.35 : delta;
    event.preventDefault();
    this.cdr.markForCheck();
  }

  onReviewDragEnd(): void {
    if (!this.isReviewDragging) {
      return;
    }

    this.isReviewDragging = false;

    if (
      this.reviewDragOffset <= -this.reviewSwipeThreshold &&
      this.activeReviewIndex < this.zillowReviews.length - 1
    ) {
      this.activeReviewIndex++;
    } else if (
      this.reviewDragOffset >= this.reviewSwipeThreshold &&
      this.activeReviewIndex > 0
    ) {
      this.activeReviewIndex--;
    }

    this.reviewDragOffset = 0;
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
  }

  private async loadFeaturedListings(): Promise<void> {
    this.listingsLoading = true;
    this.cdr.markForCheck();

    const pages = await fetchBrowseListingsPage(this.http, {
      types: HOME_FEATURED_TYPES,
      fields: 'card',
      filterState: {
        ...DEFAULT_FILTER_URL_STATE,
        typesToDisplay: { ...DEFAULT_FILTER_URL_STATE.typesToDisplay },
      },
      saleRent: 'sale',
      featuredOnly: true,
      limit: HOME_FEATURED_FETCH_LIMIT,
      offset: 0,
    });

    const featured: PropertyRecord[] = [];
    for (const type of HOME_FEATURED_TYPES) {
      featured.push(...(pages[type]?.items ?? []));
    }

    this.featuredListings = featured.slice(0, HOME_FEATURED_DISPLAY_LIMIT);
    this.listingsLoading = false;
    this.cdr.markForCheck();
  }

  private getReviewClientX(event: TouchEvent | MouseEvent): number {
    if (event instanceof TouchEvent) {
      return event.touches[0]?.clientX ?? event.changedTouches[0]?.clientX ?? 0;
    }
    return event.clientX;
  }
}
